export abstract class PhoneFormat {
  /**
   * Cleans a phone number string by removing all non-digit characters and applying a basic formatting.
   *
   * This method first removes any non-digit characters from the provided phone number.
   * Then, it applies a pattern that inserts a "0" at the beginning and a hyphen after the first two digits.
   *
   * @param {string} phone - The raw phone number string.
   * @returns {string} The cleaned and formatted phone number.
   */
  static clean(phone: string): string {
    return phone.replace(/\D/g, "").replace(/^(\d{2})(\d+)$/, "0$1-$2");
  }

  /**
   * Formats a cellphone number into a standardized pattern.
   *
   * The input string is cleaned of non-digit characters and limited to 11 digits.
   * The formatting applied is:
   * - The first two digits are treated as the area code and enclosed in parentheses.
   * - The next five digits are treated as the prefix.
   * - Any remaining digits (up to four) are appended after a hyphen.
   *
   * For example, an input of "11987654321" will be formatted as "(11) 98765-4321".
   *
   * @param {string} value - The raw cellphone number string.
   * @returns {string} The formatted cellphone number, or an empty string if the input is falsy.
   */
  static formatCellphone(value: string): string {
    if (!value) return "";

    const cleaned = value.replace(/\D/g, "").slice(0, 11);

    const areaCode = cleaned.slice(0, 2);
    const numberBody = cleaned.slice(2);

    let formatted = `(${areaCode}`;
    if (numberBody) {
      const prefix = numberBody.slice(0, 5);
      const suffix = numberBody.slice(5);
      formatted += `) ${prefix}`;
      if (suffix) {
        formatted += `-${suffix}`;
      }
    }
    return formatted;
  }
}
